import type { ForemanParsedResponse } from '../types/foreman';

/** Parse Foreman's structured output blocks from a message */
export function parseForemanResponse(text: string): ForemanParsedResponse {
  const result: ForemanParsedResponse = {
    text: '',
    meta: null,
    items: null,
    pricing: null,
    alert: null,
    progress: null,
    actions: null,
    choices: null,
    correction: null,
    practice: null,
    learningReport: null,
  };

  // Extract [FOREMAN_META] block
  const metaMatch = text.match(/\[FOREMAN_META\]([\s\S]*?)\[\/FOREMAN_META\]/);
  if (metaMatch) {
    const lines = metaMatch[1].trim().split('\n');
    const meta: Record<string, string> = {};
    lines.forEach((line) => {
      const [key, ...rest] = line.split(':');
      if (key) meta[key.trim()] = rest.join(':').trim();
    });
    result.meta = {
      confidence: meta.confidence || '0',
      skill: meta.skill || '',
      source: meta.source || '',
    };
  }

  // Extract [FOREMAN_ITEMS] block
  const itemsMatch = text.match(/\[FOREMAN_ITEMS\]([\s\S]*?)\[\/FOREMAN_ITEMS\]/);
  if (itemsMatch) {
    result.items = itemsMatch[1]
      .trim()
      .split('\n')
      .filter((l) => l.trim().startsWith('-'))
      .map((line) => {
        const [typePart, ...textParts] = line.replace(/^-\s*/, '').split('|');
        return {
          type: (typePart.replace('type:', '').trim() as 'ok' | 'warn' | 'info') || 'info',
          text: textParts.join('|').trim(),
        };
      });
  }

  // Extract [FOREMAN_PRICING] block
  const pricingMatch = text.match(/\[FOREMAN_PRICING\]([\s\S]*?)\[\/FOREMAN_PRICING\]/);
  if (pricingMatch) {
    const lines = pricingMatch[1]
      .trim()
      .split('\n')
      .filter((l) => l.includes('|'));
    result.pricing = {
      lines: lines.slice(0, -1).map((line) => {
        const [cat, amt, note] = line.split('|').map((s) => s.trim());
        return { cat: cat || '', amt: amt || '', note: note || '' };
      }),
      total: lines.length > 0 ? lines[lines.length - 1].split('|')[1]?.trim() || null : null,
    };
  }

  // Extract [FOREMAN_ALERT] block
  const alertMatch = text.match(/\[FOREMAN_ALERT\]([\s\S]*?)\[\/FOREMAN_ALERT\]/);
  if (alertMatch) {
    const alertData: Record<string, string> = {};
    alertMatch[1]
      .trim()
      .split('\n')
      .forEach((line) => {
        const [key, ...rest] = line.split(':');
        if (key) alertData[key.trim()] = rest.join(':').trim();
      });
    result.alert = {
      type: alertData.type || 'info',
      title: alertData.title || '',
      text: alertData.text || '',
    };
  }

  // Extract [FOREMAN_PROGRESS] block
  const progressMatch = text.match(/\[FOREMAN_PROGRESS\]([\s\S]*?)\[\/FOREMAN_PROGRESS\]/);
  if (progressMatch) {
    result.progress = progressMatch[1]
      .trim()
      .split('\n')
      .filter((l) => l.trim())
      .map((line) => ({
        label: line.replace(/^[✓○]\s*/, '').trim(),
        done: line.trim().startsWith('✓'),
      }));
  }

  // Extract [FOREMAN_ACTIONS] block
  const actionsMatch = text.match(/\[FOREMAN_ACTIONS\]([\s\S]*?)\[\/FOREMAN_ACTIONS\]/);
  if (actionsMatch) {
    result.actions = actionsMatch[1]
      .trim()
      .split('\n')
      .filter((l) => l.trim().startsWith('-'))
      .map((l) => l.replace(/^-\s*/, '').trim());
  }

  // Extract [FOREMAN_CORRECTION] block
  const correctionMatch = text.match(/\[FOREMAN_CORRECTION\]([\s\S]*?)\[\/FOREMAN_CORRECTION\]/);
  if (correctionMatch) {
    const correctionData: Record<string, string> = {};
    correctionMatch[1]
      .trim()
      .split('\n')
      .forEach((line) => {
        const [key, ...rest] = line.split(':');
        if (key) correctionData[key.trim()] = rest.join(':').trim();
      });
    result.correction = {
      field: correctionData.field || '',
      original: correctionData.original || '',
      corrected: correctionData.corrected || '',
      context: correctionData.context || '',
      corrected_by: correctionData.corrected_by || '',
    };
  }

  // Extract [FOREMAN_PRACTICE] block
  const practiceMatch = text.match(/\[FOREMAN_PRACTICE\]([\s\S]*?)\[\/FOREMAN_PRACTICE\]/);
  if (practiceMatch) {
    const practiceData: Record<string, string> = {};
    practiceMatch[1]
      .trim()
      .split('\n')
      .forEach((line) => {
        const [key, ...rest] = line.split(':');
        if (key) practiceData[key.trim()] = rest.join(':').trim();
      });
    result.practice = {
      title: practiceData.title || '',
      category: practiceData.category || '',
      priority: practiceData.priority || 'standard',
      taught_by: practiceData.taught_by || '',
    };
  }

  // Extract [FOREMAN_LEARNING_REPORT] block
  const learningMatch = text.match(/\[FOREMAN_LEARNING_REPORT\]([\s\S]*?)\[\/FOREMAN_LEARNING_REPORT\]/);
  if (learningMatch) {
    const learningData: Record<string, string> = {};
    learningMatch[1]
      .trim()
      .split('\n')
      .forEach((line) => {
        const [key, ...rest] = line.split(':');
        if (key) learningData[key.trim()] = rest.join(':').trim();
      });
    result.learningReport = {
      period: learningData.period || '',
      corrections: learningData.corrections || '0',
      practices: learningData.practices || '0',
      trend: learningData.trend || 'stable',
      accuracy: learningData.accuracy || '',
    };
  }

  // Clean text (remove all structured blocks)
  result.text = text
    .replace(/\[FOREMAN_META\][\s\S]*?\[\/FOREMAN_META\]/g, '')
    .replace(/\[FOREMAN_ITEMS\][\s\S]*?\[\/FOREMAN_ITEMS\]/g, '')
    .replace(/\[FOREMAN_PRICING\][\s\S]*?\[\/FOREMAN_PRICING\]/g, '')
    .replace(/\[FOREMAN_ALERT\][\s\S]*?\[\/FOREMAN_ALERT\]/g, '')
    .replace(/\[FOREMAN_PROGRESS\][\s\S]*?\[\/FOREMAN_PROGRESS\]/g, '')
    .replace(/\[FOREMAN_ACTIONS\][\s\S]*?\[\/FOREMAN_ACTIONS\]/g, '')
    .replace(/\[FOREMAN_CORRECTION\][\s\S]*?\[\/FOREMAN_CORRECTION\]/g, '')
    .replace(/\[FOREMAN_PRACTICE\][\s\S]*?\[\/FOREMAN_PRACTICE\]/g, '')
    .replace(/\[FOREMAN_LEARNING_REPORT\][\s\S]*?\[\/FOREMAN_LEARNING_REPORT\]/g, '')
    .trim();

  return result;
}
